import React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { SEmbedSessionResponse } from "@norma-bi/bi-api";
import { useLocation, useNavigate } from "react-router";
import { AI_DASHBOARDS_PATH, AI_SCHEDULES_PATH, CHAT_PATH } from "@/constants";
import { getPosthogConfig } from "@/posthogConfig";
import {
  sectionFromPath,
  isSEmbedHostMessage,
  type SEmbedTokenMessage,
} from "@norma-bi/s-embed-sdk";
import type { SEmbedConfig } from "@norma-bi/bi-api";
import { consumeSEmbedAnalyticsMessage } from "./hostAnalytics";
import { useSEmbedNavigationMemoryStore } from "./navigationMemoryStore";
import { sEmbedAppPath, sHostSettingsPath, sNavigationHostPath } from "./fullAppPaths";

export type SEmbedSection = "chat" | "dashboards" | "schedules";

const sectionPaths: Record<SEmbedSection, string> = {
  chat: CHAT_PATH,
  dashboards: AI_DASHBOARDS_PATH,
  schedules: AI_SCHEDULES_PATH,
};

function buildIframeSrc(
  webUiUrl: string,
  workspaceKey: string,
  appKey: string,
  section: SEmbedSection,
) {
  return `${webUiUrl}/embed/workspaces/${encodeURIComponent(workspaceKey)}/apps/${encodeURIComponent(appKey)}/${section}`;
}

const LANGUAGE_STORAGE_KEY = "s-embed-language";

/**
 * The S interface language picked in S's profile menu and saved in this browser. Until the
 * user picks one, it follows the browser's first English or Arabic language.
 */
export function savedSLanguage(): "en" | "ar" {
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    // Blocked storage falls back to the browser language.
  }
  const language =
    saved ??
    navigator.languages
      .map((tag) => tag.toLowerCase().split("-")[0])
      .find((tag) => tag === "en" || tag === "ar");
  return language === "ar" ? "ar" : "en";
}

export type UseSEmbedParams = {
  // Full query state for embed session. Hook derives iframe URL, trusted origin,
  // bootstrap token payload, and loading-driven reset behavior from this object.
  embedSession: UseQueryResult<SEmbedSessionResponse>;
  section: SEmbedSection;
  fullApp?: boolean;
  configFallback?: SEmbedConfig;
  onBusinessSelect?: (id: string) => Promise<void>;
  canPostToken?: () => boolean;
};

export function useSEmbed({
  embedSession,
  section,
  fullApp = false,
  configFallback,
  onBusinessSelect,
  canPostToken,
}: UseSEmbedParams): {
  isReady: boolean;
  iframeProps: React.IframeHTMLAttributes<HTMLIFrameElement> & {
    ref: React.RefObject<HTMLIFrameElement | null>;
  };
} {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const rememberedPathRef = React.useRef(useSEmbedNavigationMemoryStore.getState().paths[section]);
  const navigate = useNavigate();
  const location = useLocation();
  const initialDeepPath = React.useRef(
    `${location.pathname.slice(CHAT_PATH.length)}${location.search}${location.hash}`,
  );
  const setRememberedPath = useSEmbedNavigationMemoryStore((state) => state.setPath);
  const [isReady, setIsReady] = React.useState(false);
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const config = embedSession.data?.config ?? configFallback;
  const session = embedSession.data?.session;
  const iframeSrc = React.useMemo(() => {
    if (!config) return undefined;
    const url = new URL(
      fullApp
        ? `${sEmbedAppPath(config)}${initialDeepPath.current}`
        : (rememberedPathRef.current ??
            buildIframeSrc(config.webUiUrl, config.workspaceKey, config.appKey, section)),
      config.webUiUrl,
    );
    if (!fullApp) url.searchParams.set("sEmbedCacheBust", "20260826");
    // S reads language and direction separately; Foodics shows Arabic right to left.
    const language = savedSLanguage();
    url.searchParams.set("locale", language);
    url.searchParams.set("dir", language === "ar" ? "rtl" : "ltr");
    return url.toString();
  }, [config, section, fullApp]);
  const iframeOrigin = config ? new URL(config.webUiUrl).origin : null;
  const sessionMessage = React.useMemo<SEmbedTokenMessage | null>(() => {
    if (!session) {
      return null;
    }
    const posthog = getPosthogConfig();
    return {
      type: "S_EMBED_TOKEN",
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
      expiresIn: session.expiresIn,
      ...(fullApp ? { dataContext: embedSession.data?.dataContext } : {}),
      host: {
        navigation: "host",
        overlays: "host",
      },
      ...(posthog ? { posthog } : {}),
    };
  }, [session, fullApp, embedSession.data?.dataContext]);

  const postToken = React.useCallback(() => {
    const contentWindow = iframeRef.current?.contentWindow;
    if (!iframeOrigin || !sessionMessage || !contentWindow) return;
    if (fullApp && (!sessionMessage.dataContext || !canPostToken?.())) return;

    contentWindow.postMessage(sessionMessage, iframeOrigin);
    setIsReady(true);
  }, [iframeOrigin, sessionMessage, fullApp, canPostToken]);

  // Reset when switching embed surfaces, then redo the handshake.
  React.useEffect(() => {
    setIsReady(false);
    setHasLoaded(false);
  }, [iframeSrc]);

  // This effect listens for trusted messages from the S iframe. If S says “ready”, we send the token. If S says “navigation changed”, we remember the path and maybe move the BI host tab.
  React.useLayoutEffect(() => {
    if (!iframeOrigin) return;

    function handleMessage(event: MessageEvent) {
      if (event.origin !== iframeOrigin || event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      if (consumeSEmbedAnalyticsMessage(event.data)) {
        return;
      }

      if (!isSEmbedHostMessage(event.data)) return;
      const data = event.data;
      if (data?.type === "S_EMBED_READY") {
        postToken();
        return;
      }

      if (data?.type === "S_EMBED_NAVIGATION_CHANGE" && typeof data.path === "string") {
        if (fullApp && config) {
          try {
            const hostPath = sNavigationHostPath(data.path, config);
            if (hostPath) navigate(hostPath, { replace: true });
          } catch {
            /* Ignore malformed iframe URLs. */
          }
          return;
        }
        const nextSection = sectionFromPath(data.path);

        setRememberedPath(nextSection, data.path);

        if (section !== nextSection) {
          navigate(sectionPaths[nextSection]);
        }
      }

      if (fullApp && data.type === "S_EMBED_LINK") {
        try {
          const hostPath = sHostSettingsPath(data.url, window.location.origin);
          if (hostPath) navigate(hostPath);
        } catch {
          /* Ignore malformed menu links. */
        }
      }
      if (fullApp && data.type === "S_EMBED_PICKER_SELECT" && data.key === "language") {
        try {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, data.optionId === "ar" ? "ar" : "en");
        } catch {
          return; // Without storage the reload would come back in the old language.
        }
        // The host URL follows S's route, so a reload reopens this page with a new session.
        window.location.reload();
      }
      if (fullApp && data.type === "S_EMBED_PICKER_SELECT" && data.key === "business") {
        void onBusinessSelect?.(data.optionId).catch(() => {
          /* The host displays switch errors. */
        });
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [
    iframeOrigin,
    navigate,
    postToken,
    section,
    setRememberedPath,
    fullApp,
    config,
    onBusinessSelect,
  ]);

  React.useEffect(() => {
    if (embedSession.isFetching) setIsReady(false);
    if (hasLoaded && !embedSession.isFetching) postToken();
  }, [embedSession.isFetching, hasLoaded, postToken]);

  return {
    isReady,
    iframeProps: {
      ref: iframeRef,
      src: iframeSrc,
      onLoad: () => setHasLoaded(true),
      allow: "clipboard-write",
      style: !isReady ? { visibility: "hidden", position: "absolute" } : undefined,
    },
  };
}
